import { Box, Button, FontAwesomeIcon, Stack } from "@skailer-com/uikit";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { useTranslation } from "react-i18next";
import { useCallback, useRef, useState } from "react";
import { Editor } from "@tiptap/react";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { apiFiles } from "@/api/file.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiComment } from "@/types/comment.types.ts";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";

interface CommentFormProps {
  entityId: number;
  entityType: ApiEntityTypes;
  onSave: (text: string, fileUuids: string[]) => Promise<boolean>;
  comment?: ApiComment;
  isCollapsedOnInit?: boolean;
  useFiles?: boolean;
  useMentions?: boolean;
  alwaysShowSubmit?: boolean;
}

export function CommentForm({
  entityId,
  entityType,
  onSave,
  comment,
  isCollapsedOnInit = true,
  useFiles = true,
  useMentions = true,
  alwaysShowSubmit = false,
}: CommentFormProps) {
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const { replaceMentions } = useMentionsHook();

  const [text, setText] = useState(
    replaceMentions(comment?.text, comment?.mentioned_employees) || ""
  );
  const [files, setFiles] = useState<ApiFile[]>(comment?.files || []);

  const [isPending, setIsPending] = useState<boolean>(false);

  const [isCollapsed, setIsCollapsed] = useState(isCollapsedOnInit);
  const editorRef = useRef<Editor | null>(null);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(!isCollapsed);

    setTimeout(() => {
      editorRef.current?.commands.focus();
    }, 200);
  }, [isCollapsed]);

  const handleRemoveFile = useCallback(
    async (file: ApiFile) => {
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      const { error } = await apiFiles.delete(file.uuid);
      if (error) handleErrors(error);
    },
    [handleErrors]
  );

  const handleSave = useCallback(
    async (overrideText?: string) => {
      const currentText = overrideText ?? text;
      if (!currentText.trim() && files.length === 0) return;

      setIsPending(true);
      try {
        const success = await onSave(
          currentText,
          files.map((f) => f.uuid)
        );
        if (success) {
          setText("");
          setFiles([]);
          editorRef.current?.commands.clearContent();
          setIsCollapsed(true);
        }
      } finally {
        setIsPending(false);
      }
    },
    [text, files, onSave]
  );

  const handleSaveFromEditor = useCallback(
    (editor: Editor) => {
      void handleSave(editor.getHTML());
    },
    [handleSave]
  );

  if (isCollapsed) {
    return (
      <Box>
        <Button size={"sm"} variant={"secondary"} onClick={toggleCollapsed}>
          {t("common:add_comment") + "..."}
        </Button>
      </Box>
    );
  }

  return (
    <Stack gap={"sm"}>
      <RichEditor
        value={text}
        hideToolbar={true}
        label={`${t("common:add_comment")}...`}
        likeTextArea={true}
        highlightMentions={useMentions}
        ref={editorRef}
        onChange={(value) => setText(value)}
        onSave={handleSaveFromEditor}
      />
      {files.length > 0 && (
        <Stack gap={"sm"}>
          {files.map((file) => (
            <FileItem
              key={`file-${file.id}`}
              file={file}
              onRemove={() => {
                void handleRemoveFile(file);
              }}
            />
          ))}
        </Stack>
      )}
      {(alwaysShowSubmit || useFiles || text.length > 0) && (
        <Stack items={"center"} justify={"between"} direction={"horizontal"} gap={"sm"}>
          <Box>
            {useFiles && (
              <FileUpload
                entityId={entityId}
                entityType={entityType}
                afterSend={(file) => {
                  if (file) setFiles([...files, file]);
                }}
              />
            )}
          </Box>

          <Button
            type={"submit"}
            isLoading={isPending}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-paper-plane-top text-lg"} />}
            onClick={() => handleSave()}
          >
            {t("common:save")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
